import { apiClient, type Paginated } from '@/core/api';
import { triggerBlobDownload } from '@/features/finance/staff-invoices/api';
import type { DocumentStatus, TemplateType } from './constants';
import type { Document, DocumentVersion, Template, TemplateInput } from './types';

const TEMPLATES = '/documents/templates/';
const DOCUMENTS = '/documents/documents/';

// ---- Templates ----
export interface TemplateListParams {
  page?: number;
  search?: string;
  type?: TemplateType;
  is_active?: boolean;
}

export async function listTemplates(params: TemplateListParams = {}): Promise<Paginated<Template>> {
  const { data } = await apiClient.get<Paginated<Template>>(TEMPLATES, { params });
  return data;
}

/** Detects a File in the input and switches to multipart; otherwise JSON.
 *  `file === null` clears the current upload; `undefined` leaves it alone. */
function hasFilePayload(input: Partial<TemplateInput>): boolean {
  return 'file' in input && (input.file instanceof File || input.file === null);
}

function toFormData(input: Partial<TemplateInput>): FormData {
  const fd = new FormData();
  for (const [key, value] of Object.entries(input)) {
    if (key === 'file') {
      if (value instanceof File) fd.append('file', value);
      else if (value === null) fd.append('file', '');  // DRF clears FileField on empty string
      continue;
    }
    if (value === undefined || value === null) continue;
    fd.append(key, typeof value === 'boolean' ? String(value) : (value as string));
  }
  return fd;
}

/** Drop `file` from the payload — used on the JSON path so we never send a
 *  `File` (or `undefined`) over JSON. The multipart path goes via `toFormData`. */
function withoutFile<T extends { file?: unknown }>(input: T): Omit<T, 'file'> {
  const out = { ...input };
  delete out.file;
  return out;
}

export async function createTemplate(input: TemplateInput): Promise<Template> {
  if (hasFilePayload(input)) {
    const { data } = await apiClient.post<Template>(TEMPLATES, toFormData(input));
    return data;
  }
  const { data } = await apiClient.post<Template>(TEMPLATES, withoutFile(input));
  return data;
}

export async function updateTemplate(id: number, input: Partial<TemplateInput>): Promise<Template> {
  if (hasFilePayload(input)) {
    const { data } = await apiClient.patch<Template>(`${TEMPLATES}${id}/`, toFormData(input));
    return data;
  }
  const { data } = await apiClient.patch<Template>(`${TEMPLATES}${id}/`, withoutFile(input));
  return data;
}

export async function deleteTemplate(id: number): Promise<void> {
  await apiClient.delete(`${TEMPLATES}${id}/`);
}

export interface RenderTemplateInput {
  context: Record<string, string>;
  name?: string;
}

/** Render a template (+ merge data) into a new Document. */
export async function renderTemplate(id: number, input: RenderTemplateInput): Promise<Document> {
  const { data } = await apiClient.post<Document>(`${TEMPLATES}${id}/render/`, input);
  return data;
}

// ---- Documents (read-only + actions) ----
export interface DocumentListParams {
  page?: number;
  page_size?: number;
  search?: string;
  type?: TemplateType;
  status?: DocumentStatus;
  template?: number;
  /** Attachments for one entity: the target model's ContentType id + its pk. */
  content_type?: number;
  object_id?: string;
}

export async function listDocuments(params: DocumentListParams = {}): Promise<Paginated<Document>> {
  const { data } = await apiClient.get<Paginated<Document>>(DOCUMENTS, { params });
  return data;
}

/** Upload a file as a Document linked to any entity (generic FK). Multipart. */
export async function uploadDocument(input: {
  file: File;
  name: string;
  content_type: number;
  object_id: string;
}): Promise<Document> {
  const fd = new FormData();
  fd.append('file', input.file);
  fd.append('name', input.name);
  fd.append('type', 'other');
  fd.append('content_type', String(input.content_type));
  fd.append('object_id', input.object_id);
  const { data } = await apiClient.post<Document>(DOCUMENTS, fd);
  return data;
}

export async function deleteDocument(id: number): Promise<void> {
  await apiClient.delete(`${DOCUMENTS}${id}/`);
}

export async function getDocument(id: number): Promise<Document> {
  const { data } = await apiClient.get<Document>(`${DOCUMENTS}${id}/`);
  return data;
}

/** Fetch a document's file as a Blob via the API (auth + CORS handled) — the raw
 * media URL can't be fetched cross-origin (preflight 405 / 401). For inline
 * preview and authenticated download. */
export async function fetchDocumentFileBlob(id: number): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${DOCUMENTS}${id}/download/`, {
    responseType: 'blob',
  });
  return data;
}

export async function downloadDocumentFile(id: number, filename: string): Promise<void> {
  triggerBlobDownload(await fetchDocumentFileBlob(id), filename);
}

/** Native in-app signing — marks the document SIGNED. */
export async function signDocument(id: number): Promise<Document> {
  const { data } = await apiClient.post<Document>(`${DOCUMENTS}${id}/sign/`);
  return data;
}

/** Manager sets a document's lifecycle status (e.g. Draft → Submitted). */
export async function setDocumentStatus(id: number, status: DocumentStatus): Promise<Document> {
  const { data } = await apiClient.post<Document>(`${DOCUMENTS}${id}/set-status/`, { status });
  return data;
}

/** Manager re-classifies a document's type (uploads default to "other"). */
export async function setDocumentType(id: number, type: TemplateType): Promise<Document> {
  const { data } = await apiClient.post<Document>(`${DOCUMENTS}${id}/set-type/`, { type });
  return data;
}

// ---- Wave 12: versioning -------------------------------------------------

export async function listDocumentVersions(id: number): Promise<DocumentVersion[]> {
  const { data } = await apiClient.get<DocumentVersion[]>(`${DOCUMENTS}${id}/versions/`);
  return data;
}

export interface EditDocumentContentInput {
  rendered_content: string;
  change_note?: string;
}

export async function editDocumentContent(
  id: number, input: EditDocumentContentInput,
): Promise<Document> {
  const { data } = await apiClient.post<Document>(`${DOCUMENTS}${id}/edit-content/`, input);
  return data;
}

export async function restoreDocumentVersion(
  id: number, versionNumber: number,
): Promise<Document> {
  const { data } = await apiClient.post<Document>(
    `${DOCUMENTS}${id}/restore-version/`, { version_number: versionNumber },
  );
  return data;
}
